import { useCallback, useRef, useState } from 'react';
import { searchProducts } from '../services';
import type { ProductFilterOption } from '../types';

const DEFAULT_SIZE = 50;
const LOAD_MORE_INCREMENT = 100;
const MAX_SIZE = 1000;

export const useProductsSearch = () => {
  const [list, setList] = useState<ProductFilterOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [currentSize, setCurrentSize] = useState(DEFAULT_SIZE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProducts = useCallback(async (query: string, size: number) => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const response = await searchProducts({ query, size });
      setList(response);
    } catch {
      setList([]);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setSearch(value);
      setCurrentSize(DEFAULT_SIZE);

      timerRef.current = setTimeout(() => {
        fetchProducts(value, DEFAULT_SIZE);
      }, 500);
    },
    [fetchProducts],
  );

  const handleLoadMore = useCallback(() => {
    if (list.length >= MAX_SIZE) return;

    const newSize = currentSize + LOAD_MORE_INCREMENT;
    setCurrentSize(newSize);
    fetchProducts(search, newSize);
  }, [currentSize, search, list.length, fetchProducts]);

  const handleOpen = useCallback(() => {
    if (list.length === 0) {
      fetchProducts('', DEFAULT_SIZE);
    }
  }, [list.length, fetchProducts]);

  return {
    data: list,
    isLoading,
    loadError,
    search,
    currentSize,
    maxSize: MAX_SIZE,
    handleSearch,
    handleLoadMore,
    handleOpen,
  };
};
